import React, { useState, useRef } from 'react';
import { supabase } from '../supabase';

function parseCSV(text) {
  const lines = text.trim().split('\n');
  const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim());
  return lines.slice(1).map(line => {
    const vals = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; continue; }
      if (line[i] === ',' && !inQ) { vals.push(cur); cur = ''; continue; }
      cur += line[i];
    }
    vals.push(cur);
    return Object.fromEntries(headers.map((h, i) => [h, vals[i]?.trim() || '']));
  });
}

function cleanNum(v) {
  if (!v || v === '—') return null;
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''));
  return isNaN(n) ? null : n;
}

function cleanStr(v) {
  if (!v || v === '—') return null;
  return v.trim() || null;
}

function cleanDate(v) {
  if (!v || v === '—') return null;
  try {
    const d = new Date(v);
    if (isNaN(d)) return null;
    return d.toISOString().split('T')[0];
  } catch { return null; }
}

export default function ImportModal({ type, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const titles = { projects: 'Projects', customers: 'Customers', payments: 'Payments' };
  const expectedCols = {
    projects: ['Code', 'Project', 'Status', 'Service', 'Council', 'Planning Ref', 'Order Date', 'Delivery Date'],
    customers: ['Code', 'Name', 'Company', 'Type', 'Email', 'Phone'],
    payments: ['Code', 'Agreed Fee', 'Paid', 'Pending', 'Invoice 1', 'Invoice Date 1', 'Invoice 2', 'Invoice Date 2'],
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const rows = parseCSV(ev.target.result);
        setPreview(rows.slice(0, 5));
      } catch (err) {
        setError('Could not parse CSV. Make sure it was exported from this system.');
      }
    };
    reader.readAsText(f);
  };

  const runImport = async () => {
    if (!file) return;
    setImporting(true); setError(''); setResult(null);

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const rows = parseCSV(ev.target.result);
        let updated = 0, created = 0, errors = 0;

        for (const row of rows) {
          try {
            if (type === 'customers') {
              const code = cleanStr(row['Code']);
              if (!code) continue;
              const { data: existing } = await supabase.from('customers').select('id').eq('customer_code', code).single();
              const payload = {
                full_name: cleanStr(row['Name']) || undefined,
                company: cleanStr(row['Company']),
                type: cleanStr(row['Type']) || undefined,
                email: cleanStr(row['Email']),
                phone: cleanStr(row['Phone']),
                notes: cleanStr(row['Notes']),
              };
              Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
              if (existing) {
                await supabase.from('customers').update(payload).eq('id', existing.id);
                updated++;
              } else {
                await supabase.from('customers').insert({ ...payload, customer_code: code, created_by_name: 'Import' });
                created++;
              }

            } else if (type === 'projects') {
              const code = cleanStr(row['Code']);
              if (!code) continue;
              const { data: existing } = await supabase.from('projects').select('id').eq('project_code', code).single();
              const payload = {
                project_name: cleanStr(row['Project']) || undefined,
                service_type: cleanStr(row['Service']) || undefined,
                council: cleanStr(row['Council']),
                planning_ref: cleanStr(row['Planning Ref']),
                status: cleanStr(row['Status']) || undefined,
                order_date: cleanDate(row['Order Date']),
                delivery_date: cleanDate(row['Delivery Date']),
                notes: cleanStr(row['Notes']),
              };
              Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
              if (existing) {
                await supabase.from('projects').update(payload).eq('id', existing.id);
                updated++;
              } else {
                await supabase.from('projects').insert({ ...payload, project_code: code, created_by_name: 'Import' });
                created++;
              }

            } else if (type === 'payments') {
              const code = cleanStr(row['Code']);
              if (!code) continue;
              const { data: proj } = await supabase.from('projects').select('id').eq('project_code', code).single();
              if (!proj) { errors++; continue; }
              const { data: fin } = await supabase.from('financials').select('id').eq('project_id', proj.id).single();
              const payload = {
                agreed_fee: cleanNum(row['Agreed Fee']),
                paid_amount: cleanNum(row['Paid']),
                pending_amount: cleanNum(row['Pending']),
                invoice_no_1: cleanStr(row['Invoice 1']),
                invoice_date_1: cleanDate(row['Invoice Date 1']),
                invoice_no_2: cleanStr(row['Invoice 2']),
                invoice_date_2: cleanDate(row['Invoice Date 2']),
                payment_notes: cleanStr(row['Payment Notes']),
              };
              Object.keys(payload).forEach(k => payload[k] === null && delete payload[k]);
              if (fin) {
                await supabase.from('financials').update(payload).eq('id', fin.id);
                updated++;
              } else {
                await supabase.from('financials').insert({ ...payload, project_id: proj.id });
                created++;
              }
            }
          } catch (e) {
            errors++;
          }
        }
        setResult({ updated, created, errors, total: rows.length });
        if (updated + created > 0) onDone();
      } catch (e) {
        setError('Import failed: ' + e.message);
      }
      setImporting(false);
    };
    reader.readAsText(file);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>Import {titles[type]}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--text)' }}>How it works:</strong><br />
            Export CSV → edit in Excel/Numbers → import back.<br />
            Existing records are <strong>updated</strong> by code. New codes are <strong>created</strong>. Nothing is deleted.
          </div>

          <div style={{ marginBottom: 20 }}>
            <label>Expected columns</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {expectedCols[type].map(c => (
                <span key={c} style={{ background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: 11, padding: '3px 9px', borderRadius: 4, fontWeight: 600 }}>{c}</span>
              ))}
            </div>
          </div>

          <div
            style={{ border: '2px dashed var(--border2)', borderRadius: 'var(--radius)', padding: '28px', textAlign: 'center', cursor: 'pointer', marginBottom: 16 }}
            onClick={() => fileRef.current.click()}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
            <p style={{ color: 'var(--text2)', fontSize: 13 }}>{file ? file.name : 'Click to select CSV file'}</p>
            {file && <p style={{ color: 'var(--text3)', fontSize: 11, marginTop: 4 }}>Click to change</p>}
          </div>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFile} />

          {preview && !result && (
            <div style={{ marginBottom: 16 }}>
              <label>Preview ({preview.length} of {preview.length}+ rows)</label>
              <div style={{ overflow: 'auto', maxHeight: 160, border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: 6 }}>
                <table className="table" style={{ fontSize: 11 }}>
                  <thead>
                    <tr>{Object.keys(preview[0]).slice(0, 6).map(k => <th key={k}>{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i}>{Object.values(row).slice(0, 6).map((v, j) => <td key={j}>{v || '—'}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div style={{ background: result.errors > 0 ? 'var(--yellow-dim)' : 'var(--green-dim)', borderRadius: 'var(--radius)', padding: '14px 16px', fontSize: 13 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>Import complete</div>
              <div style={{ color: 'var(--text2)', display: 'flex', gap: 16 }}>
                <span>✓ {result.updated} updated</span>
                <span>✓ {result.created} created</span>
                {result.errors > 0 && <span style={{ color: 'var(--red)' }}>✗ {result.errors} errors</span>}
              </div>
            </div>
          )}

          {error && <p style={{ color: 'var(--red)', fontSize: 13, marginTop: 10 }}>{error}</p>}
        </div>
        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose}>{result ? 'Close' : 'Cancel'}</button>
          {!result && <button className="btn-primary" onClick={runImport} disabled={!file || importing}>{importing ? 'Importing...' : 'Import CSV'}</button>}
        </div>
      </div>
    </div>
  );
}
